import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { FormTask, FormValues } from '../AddTaskToCRMModal'
import { Controller, useFormContext } from 'react-hook-form'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import DatePicker from 'react-datepicker'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ZocksDataTable } from '../../../../../components/DataTable/ZocksDataTable'
import { useLazyGetIntegrationTeammatesQuery } from '../../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { User } from '../../../../../models/User'
import { MultiSelectDropdown } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { taskCategoryEntityType } from './utils'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { getContactDropdownComponent } from '../../../ContactDropdownItems'
import { IntegrationServiceId } from '../../../../../common/integrations'

export interface TasksTableProps {
  serviceId: string
  isAllSelected: boolean
  anySelected: boolean
  onSelectAll: (checked: boolean) => void
  tasks: FormTask[]
  taskCategoryOptions: SelectOption[]
  assignedToOptions: SelectOption[]
  onEditTask: (taskId: string) => void
  onDeleteTask: (taskId: string) => void
  editDisabled: boolean
}

export type TaskCategoryEntityTypeConfig = {
  api: string
  dto: string
}

const _STATUS_OPTIONS: SelectOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'close', label: 'Close' },
]

export const TasksTable: React.FC<TasksTableProps> = ({
  serviceId,
  isAllSelected,
  anySelected,
  onSelectAll,
  tasks,
  taskCategoryOptions,
  assignedToOptions,
  onEditTask,
  onDeleteTask,
  editDisabled,
}) => {
  const { control } = useFormContext<FormValues>()
  const selectAllCheckboxRef = React.useRef<HTMLInputElement>(null)
  const { tracked } = useTracking()
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = anySelected && !isAllSelected
    }
  }, [anySelected, isAllSelected])

  const priorityOptions = [
    { label: 'Low', id: 'Low' },
    { label: 'Medium', id: 'Medium' },
    { label: 'High', id: 'High' },
  ]

  const handleTaskFieldUpdate = tracked(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    (field: any, value: unknown, type: string) => {
      field.onChange(value)
    },
    {
      eventName: TrackEvent.TASK_UPDATED,
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventDetailsExtractor: (field: any, value: unknown, type: string) => {
        return {
          type,
        }
      },
    },
  )

  const columns: TableColumn<FormTask & { id: string }>[] = [
    {
      name: (
        <Form.Check
          ref={selectAllCheckboxRef}
          id='select-all-tasks-header-checkbox'
          checked={isAllSelected}
          onChange={() => onSelectAll(!anySelected)}
          title='Select all'
        />
      ),
      width: '3rem',
      button: true,
      ignoreRowClick: true,
      selector: (row) => row.selected,
      cell: (row, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.selected`}
          control={control}
          render={({ field }) => <Form.Check id={`select-task-checkbox-${row.id}`} checked={field.value} onChange={field.onChange} />}
        />
      ),
    },
    {
      name: 'Task',
      wrap: true,
      grow: 2,
      cell: (row) => (
        <span>
          <strong>
            {row.task}
            {row.description ? ':' : ''}
          </strong>{' '}
          {row.description}
        </span>
      ),
    },
    {
      name: 'Due date',
      wrap: true,
      width: '16%',
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.dueDate`}
          control={control}
          rules={{ required: 'Due date is required.' }}
          render={({ field, fieldState }) => (
            <div className={'task-date-row'}>
              <label htmlFor={`tasks-date-${rowIndex}`} className={'d-flex align-items-center '}>
                <i className={'bi bi-calendar ps-3'} />
                <DatePicker
                  id={`tasks-date-${rowIndex}`}
                  className='form-control border-0'
                  selected={field.value}
                  onChange={(date) => handleTaskFieldUpdate(field, date, 'dueDateChanged')}
                  todayButton='Today'
                  popperProps={{
                    strategy: 'fixed',
                  }}
                />
              </label>
              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
            </div>
          )}
        />
      ),
    },
    {
      name: 'Assigned To',
      wrap: true,
      width: '15%',
      cell: (_, rowIndex) => (
        <Controller
          name={serviceId === 'redtail' ? `tasks.${rowIndex}.multipleAssignedTo` : `tasks.${rowIndex}.assignedTo`}
          control={control}
          rules={{ required: 'Assigned to is required.' }}
          render={({ field, fieldState }) => {
            return (
              <div>
                <AssignedToDropdown
                  serviceId={serviceId}
                  selectedAssignedToIds={field.value ?? []}
                  onOptionSelected={(assignedTo) => handleTaskFieldUpdate(field, assignedTo, 'assignedToChanged')}
                  initialOptions={assignedToOptions}
                  dropdownName={field.name}
                />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </div>
            )
          }}
        />
      ),
    },
    {
      name: 'Priority',
      width: '15%',
      wrap: true,
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.priority`}
          control={control}
          render={({ field }) => (
            <SelectDropdown
              initialSelectedOption={priorityOptions.find((option) => option.id === field.value)}
              options={priorityOptions}
              className='py-2 zocks-px-0.75 border-0'
              onOptionSelected={(option) => handleTaskFieldUpdate(field, option.id, 'priorityChanged')}
              placeholder='Select your priority'
            />
          )}
        />
      ),
    },
    {
      name: 'Category',
      width: '15%',
      wrap: true,
      omit: !((Object.keys(taskCategoryEntityType).includes(serviceId) && taskCategoryOptions.length > 1) || serviceId === 'smartoffice'),
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.category`}
          control={control}
          render={({ field }) => (
            <CategoryColumnDropdown
              selectedTaskCategoryId={field.value}
              taskCategoryOptions={taskCategoryOptions}
              onOptionSelected={(category) => handleTaskFieldUpdate(field, category, 'categoryChanged')}
            />
          )}
        />
      ),
    },
    {
      name: 'Status',
      width: '11%',
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.status`}
          control={control}
          render={({ field }) => (
            <StatusColumnDropdown selectedTaskStatusId={field.value} taskStatusOptions={_STATUS_OPTIONS} onOptionSelected={field.onChange} />
          )}
        />
      ),
    },
    {
      name: '',
      width: '5%',
      right: true,
      cell: (row) => (
        <div className='d-flex justify-content-center p-0'>
          <Button onClick={() => onDeleteTask(row.id)} className='delete-task-button p-0' aria-label='Delete Task'>
            <i className='bi bi-trash' />
          </Button>
          {!editDisabled ? (
            <Button onClick={() => onEditTask(row.id)} className='edit-task-button p-0 ps-3' aria-label='Edit Task'>
              <i className='bi bi-pencil-square' />
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div className='add-to-crm-modal-tasks-table'>
      <ZocksDataTable columns={columns} data={tasks} fixedHeader fixedHeaderScrollHeight='100%' />
    </div>
  )
}

interface CategoryColumnDropdownProps {
  selectedTaskCategoryId: string | null
  taskCategoryOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

const CategoryColumnDropdown: React.FC<CategoryColumnDropdownProps> = ({ selectedTaskCategoryId, taskCategoryOptions, onOptionSelected }) => {
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(() =>
    selectedTaskCategoryId ? (taskCategoryOptions.find((o) => o.id === selectedTaskCategoryId) ?? null) : null,
  )

  return (
    <SelectDropdown
      initialSelectedOption={selectedCategory}
      options={taskCategoryOptions}
      className='py-2 zocks-px-0.75 border-0'
      onOptionSelected={(option) => {
        setSelectedCategory(option.id === null ? null : option)
        onOptionSelected(option.id)
      }}
      placeholder='Select…'
    />
  )
}

interface StatusColumnDropdownProps {
  selectedTaskStatusId: string | null
  taskStatusOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

const StatusColumnDropdown: React.FC<StatusColumnDropdownProps> = ({ selectedTaskStatusId, taskStatusOptions, onOptionSelected }) => {
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(() =>
    selectedTaskStatusId ? (taskStatusOptions.find((o) => o.id === selectedTaskStatusId) ?? null) : null,
  )

  return (
    <SelectDropdown
      initialSelectedOption={selectedStatus}
      options={taskStatusOptions}
      className='py-2 zocks-px-0.75 border-0'
      onOptionSelected={(option) => {
        setSelectedStatus(option.id === null ? null : option)
        onOptionSelected(option.id)
      }}
      placeholder='Select…'
    />
  )
}

interface AssignedToDropdownProps {
  serviceId: string
  selectedAssignedToIds: string | string[] | null
  onOptionSelected: (optionId: string[] | string | null) => void
  initialOptions: SelectOption[]
  dropdownName: string
}

const AssignedToDropdown: React.FC<AssignedToDropdownProps> = ({ serviceId, selectedAssignedToIds, onOptionSelected, initialOptions, dropdownName }) => {
  const enableSearchServiceIds = ['salesforce', 'practifi', 'xlr8', 'smartoffice', 'elements', 'blackdiamond']
  const accountId = useAppSelector(selectAccountId)
  const [searchedAssignedToOptions, setSearchedAssignedToOptions] = useState<SelectOption[] | null>(null)
  const [triggerGetIntegrationTeammatesQuery] = useLazyGetIntegrationTeammatesQuery()

  const getAssignedToOptions = useCallback(
    (users: User[]) => {
      return users.map((user) => {
        const contactIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
        const contactLabel = `${user.name ? user.name : ''}${user.email ? ` (${user.email})` : ''}`
        return { id: contactIntegration!.externalId, label: contactLabel }
      })
    },
    [serviceId],
  )

  const handleSearchInputChange = useCallback(
    async (searchInput: string) => {
      // Prevent API call if search input is empty or null
      if (!searchInput) {
        setSearchedAssignedToOptions(null) // Reset to show initial options
        return
      }

      try {
        const response = await triggerGetIntegrationTeammatesQuery({
          accountId,
          connector: serviceId,
          filters: { name: searchInput },
        }).unwrap()
        if (response && response.data) {
          setSearchedAssignedToOptions(getAssignedToOptions(response.data))
        }
      } catch (e) {
        console.error('Failed to search teammates:', e)
      }
    },
    [accountId, serviceId, triggerGetIntegrationTeammatesQuery, getAssignedToOptions],
  )

  const options = useMemo(() => searchedAssignedToOptions ?? initialOptions, [searchedAssignedToOptions, initialOptions])

  const selectedOption = useMemo(() => initialOptions.find((option) => option.id === selectedAssignedToIds) ?? null, [initialOptions, selectedAssignedToIds])

  if (serviceId === 'redtail') {
    const currentSelectedIds = Array.isArray(selectedAssignedToIds) ? selectedAssignedToIds : selectedAssignedToIds ? [selectedAssignedToIds] : []

    return (
      <MultiSelectDropdown
        name={dropdownName}
        options={options}
        className='py-2 zocks-px-0.75 border-0'
        initialSelectedOptions={currentSelectedIds}
        onSelectedChanged={onOptionSelected}
        placeholder='Select assignee'
      />
    )
  }

  return (
    <SelectDropdown
      initialSelectedOption={selectedOption}
      options={options}
      className='py-2 zocks-px-0.75 border-0'
      onOptionSelected={(option) => onOptionSelected(option.id)}
      DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
      searchable={true}
      onSearchInputChange={enableSearchServiceIds.includes(serviceId) ? handleSearchInputChange : undefined}
      placeholder='Select assignee'
    />
  )
}
